import React, { useState, useMemo, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import { 
     Plus, Trash2, Link, Edit, Play, Square, RotateCcw, 
     Lock, Unlock, RefreshCw, CalendarPlus, XCircle, 
     BarChart2, Loader2 
} from 'lucide-react';
import { Server as ServerType, Client } from '../../types';
import { api } from '../../utils/api';
import toast from 'react-hot-toast';
import { useSearchParams, useNavigate } from 'react-router-dom';
// Eliminamos los imports de 'recharts' que ya no se usan aquí
import { ServerTrafficModal } from './ServerTrafficModal.tsx'; // <-- Correcto, importamos el archivo

// =================================================================
//  BLOQUE DUPLICADO ELIMINADO
//  (El código de 'const ServerTrafficModal...' que estaba aquí se ha borrado)
// =================================================================


interface ServerManagementProps {}

export const ServerManagement: React.FC<ServerManagementProps> = () => {
   const {
     servers = [],
     clients = [],
     addServer, deleteServer, refreshData, assignServer,
     updateServer, toggleServerLock, renewServer
   } = useData();

   const [searchParams] = useSearchParams();
   const navigate = useNavigate();
   const clientIdFilter = searchParams.get('clientId');
   const filterType = searchParams.get('filter');

   const [showCreateModal, setShowCreateModal] = useState(false);
   const [showEditModal, setShowEditModal] = useState(false);
   const [showAssignModal, setShowAssignModal] = useState(false);
   const [showTrafficModal, setShowTrafficModal] = useState<ServerType | null>(null);
   const [selectedServer, setSelectedServer] = useState<ServerType | null>(null);
   const [editingServer, setEditingServer] = useState<ServerType | null>(null);
   const [loadingServers, setLoadingServers] = useState<Record<string, string>>({});
   const [newServer, setNewServer] = useState({
     name: '', ip: '', sshUser: '', sshPass: '', royaleServiceId: '', expirationDate: '', provider: 'dcimanager' as 'royale' | 'dcimanager', catalogRef: '', stripePriceId: ''
   });
   const [assignModalClientIds, setAssignModalClientIds] = useState<string[]>([]);

   const isExpiringSoon = (dateString?: string) => {
     if (!dateString) return false;
     const expirationDate = new Date(dateString);
     const sevenDaysFromNow = new Date();
     sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
     return expirationDate <= sevenDaysFromNow;
   };

   const filteredServers = useMemo(() => {
     let result = [...servers];
     if (clientIdFilter) {
       result = result.filter(s => s.assignedTo?.includes(clientIdFilter));
     }
     if (filterType === 'expiring') {
       result = result.filter(s => isExpiringSoon(s.expirationDate));
     }
     result.sort((a, b) => {
         const expiringA = isExpiringSoon(a.expirationDate);
         const expiringB = isExpiringSoon(b.expirationDate);
         if (expiringA && !expiringB) return -1;
         if (!expiringA && expiringB) return 1;
         const dateA = a.expirationDate ? new Date(a.expirationDate).getTime() : Infinity;
         const dateB = b.expirationDate ? new Date(b.expirationDate).getTime() : Infinity;
         if (dateA === dateB) return 0;
         return dateA - dateB;
     });
     return result;
   }, [servers, clientIdFilter, filterType]);

   const clientName = clientIdFilter ? clients.find(c => c.id === clientIdFilter)?.name : '';

   const formatDate = (dateString?: string) => {
     if (!dateString) return 'N/A';
     try {
         const date = new Date(dateString);
         if (isNaN(date.getTime())) {
             const parts = dateString.split('-');
             if (parts.length === 3) {
                 const year = parseInt(parts[0], 10);
                 const month = parseInt(parts[1], 10) - 1;
                 const day = parseInt(parts[2], 10);
                 const isoDate = new Date(Date.UTC(year, month, day));
                 if (!isNaN(isoDate.getTime())) {
                     return isoDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
                 }
             }
             return 'Fecha Inválida';
         }
         return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
     } catch (e) {
         console.error("Error formateando fecha:", dateString, e);
         return 'Fecha Inválida';
     }
   };

   const handleServerAction = async (serverId: string, action: 'start' | 'stop' | 'restart') => {
     setLoadingServers(prev => ({ ...prev, [serverId]: action }));
     const toastId = toast.loading(`Enviando comando ${action}...`);
     try {
       await api.executeServerAction(serverId, action);
       setTimeout(() => {
         toast.success(`Comando ${action} enviado con éxito.`, { id: toastId });
       }, 0);
       setTimeout(() => refreshData({ setLoading: false }), 2500);
     } catch (error: any) {
       setTimeout(() => {
         toast.error(`Error al ejecutar la acción: ${error.message || 'Error desconocido'}`, { id: toastId });
       }, 0);
     } finally {
       setLoadingServers(prev => {
         const newState = { ...prev };
         delete newState[serverId];
         return newState;
       });
     }
   };

   const handleRenewServer = async (server: ServerType) => {
     if (confirm(`¿Está seguro de que desea renovar el servidor "${server.name}" por un mes?`)) {
       const toastId = toast.loading('Renovando servidor...');
       try {
         await api.renewServer(server.id);
         await refreshData({ setLoading: false });
         setTimeout(() => {
           toast.success('Servidor renovado con éxito.', { id: toastId });
         }, 0);
       } catch (error: any) {
         setTimeout(() => {
           toast.error(`Error al renovar el servidor: ${error.message || 'Error desconocido'}`, { id: toastId });
         }, 0);
       }
     }
   };

   const handleCreateServer = async (e: React.FormEvent) => {
     e.preventDefault();
     const toastId = toast.loading('Creando servidor...');
     try {
         await addServer(newServer as any);
         setShowCreateModal(false);
         setNewServer({ name: '', ip: '', sshUser: '', sshPass: '', royaleServiceId: '', expirationDate: '', provider: 'dcimanager', catalogRef: '', stripePriceId: '' });
         await refreshData();
         setTimeout(() => { toast.success('Servidor creado.', { id: toastId }) }, 0);
     } catch (error: any) {
          setTimeout(() => { toast.error(`Error al crear servidor: ${error.message || 'Error desconocido'}`, { id: toastId }) }, 0);
     }
   };

   const handleEditClick = (server: ServerType) => {
     const formattedDate = server.expirationDate ? new Date(server.expirationDate).toISOString().split('T')[0] : '';
     setEditingServer({ ...server, expirationDate: formattedDate });
     setShowEditModal(true);
   };

   const handleUpdateServer = async (e: React.FormEvent) => {
     e.preventDefault();
     if (!editingServer) return;
     const toastId = toast.loading('Actualizando servidor...');
     try {
       const { assignedTo, ...serverData } = editingServer;
       const dataToSend = {
         ...serverData,
         expirationDate: serverData.expirationDate ? serverData.expirationDate : null
       };
       await updateServer(editingServer.id, dataToSend);
       setShowEditModal(false);
       setEditingServer(null);
       await refreshData();
       setTimeout(() => { toast.success('Servidor actualizado con éxito.', { id: toastId }) }, 0);
     } catch (error: any) {
       setTimeout(() => { toast.error(`Error al actualizar el servidor: ${error.message || 'Error desconocido'}`, { id: toastId }) }, 0);
     }
   };

   const handleDeleteServer = async (serverId: string) => {
     if (confirm('¿Está seguro de eliminar este servidor? Esta acción no se puede deshacer.')) {
       const toastId = toast.loading('Eliminando servidor...');
       try {
         await deleteServer(serverId);
         await refreshData();
         setTimeout(() => { toast.success('Servidor eliminado.', { id: toastId }) }, 0);
       } catch (error: any) {
         setTimeout(() => { toast.error(`Error al eliminar el servidor: ${error.message || 'Error desconocido'}`, { id: toastId }) }, 0);
       }
     }
   };

   const handleAssignClick = (server: ServerType) => {
     setSelectedServer(server);
     setAssignModalClientIds(server.assignedTo || []);
     setShowAssignModal(true);
   };

   const handleAssignCheckboxChange = (clientId: string, isChecked: boolean) => {
       setAssignModalClientIds(prevIds => {
           if (isChecked) {
               return prevIds.includes(clientId) ? prevIds : [...prevIds, clientId];
           } else {
               return prevIds.filter(id => id !== clientId);
           }
       });
   };

   const handleAssignServer = async () => {
     if (selectedServer) {
       const toastId = toast.loading('Guardando asignaciones...');
       try {
         await assignServer(selectedServer.id, assignModalClientIds);
         setShowAssignModal(false);
         setSelectedServer(null);
         setAssignModalClientIds([]);
         setTimeout(() => { toast.success('Asignación guardada.', { id: toastId }) }, 0);
       } catch (error: any) {
         setTimeout(() => { toast.error(`Error al guardar la asignación: ${error.message || 'Error desconocido'}`, { id: toastId }) }, 0);
       }
     }
   };

   const handleToggleLock = async (serverId: string) => {
     const toastId = toast.loading('Cambiando estado de bloqueo...');
     try {
       await toggleServerLock(serverId);
       setTimeout(() => { toast.success('Estado de bloqueo cambiado.', { id: toastId }) }, 0);
     } catch (error: any) {
       setTimeout(() => { toast.error(`Error al cambiar el estado de bloqueo: ${error.message || 'Error desconocido'}`, { id: toastId }) }, 0);
     }
   };

   const getServerStatusColor = (status?: ServerType['status']) => (status === 'online' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800');
   const getServerStatusText = (status?: ServerType['status']) => (status === 'online' ? 'En línea' : 'Fuera de línea');

   return (
     <div className="space-y-6 p-4 md:p-6">
         <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
           <div className="flex items-center gap-2 md:gap-4">
             <h2 className="text-xl md:text-2xl font-bold text-gray-900">Gestión de Servidores</h2>
             <button onClick={() => refreshData()} className="p-2 rounded-full hover:bg-gray-100 transition-colors" title="Actualizar datos">
               <RefreshCw className="w-5 h-5 text-gray-600" />
             </button>
           </div>
           <button onClick={() => setShowCreateModal(true)} className="w-full md:w-auto bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 flex items-center justify-center md:justify-start gap-2 transition-colors">
             <Plus className="w-5 h-5" />
             <span>Nuevo Servidor</span>
           </button>
         </div>

         {(clientIdFilter || filterType === 'expiring') && (
           <div className="bg-blue-50 border border-blue-200 text-blue-800 p-3 md:p-4 rounded-md flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
             <div>
               <p className="font-semibold text-sm">Filtro Activo</p>
               {clientIdFilter && <p className="text-xs md:text-sm">Mostrando solo servidores asignados a: <strong>{clientName}</strong></p>}
               {filterType === 'expiring' && <p className="text-xs md:text-sm">Mostrando solo servidores que expiran en los próximos 7 días.</p>}
             </div>
             <button onClick={() => navigate('/admin/servers')} className="flex items-center text-xs md:text-sm font-semibold text-blue-600 hover:text-blue-800 transition-colors">
               <XCircle className="w-4 h-4 mr-1" />
               Quitar Filtro
             </button>
           </div>
          )}

         <div className="bg-white rounded-xl shadow-lg border overflow-x-auto">
           <table className="min-w-full whitespace-nowrap">
             <thead className="bg-slate-100 border-b-2 border-slate-200">
               <tr>
                 <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Servidor</th>
                 <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">IP</th>
                 <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Estado</th>
                 <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Vencimiento</th>
                 <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Clientes</th>
                 <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Acciones</th>
               </tr>
             </thead>
             <tbody className="divide-y divide-gray-200">
               {filteredServers.length === 0 ? (
                 <tr>
                   <td colSpan={6} className="text-center py-10 text-gray-500">No hay servidores que coincidan con el filtro.</td>
                 </tr>
               ) : (
                 filteredServers.map((server) => {
                   const assignedClients = (server.assignedTo || []).map(clientId => clients.find(c => c.id === clientId)).filter(Boolean) as Client[];
                   const isExpiring = isExpiringSoon(server.expirationDate);
                   return (
                     <tr key={server.id} className="odd:bg-white even:bg-slate-50 hover:bg-blue-50 transition-colors">
                       <td className="px-4 py-3 font-medium text-gray-800">{server.name}</td>
                       <td className="px-4 py-3 font-mono text-gray-600 text-sm">{server.ip}</td>
                       <td className="px-4 py-3">
                           <span className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${getServerStatusColor(server.status)}`}>
                               {getServerStatusText(server.status)}
                           </span>
                       </td>
                       <td className={`px-4 py-3 font-medium text-sm ${isExpiring ? 'text-red-600 font-bold animate-pulse' : 'text-gray-600'}`}>
                           {formatDate(server.expirationDate)}
                       </td>
                       <td className="px-4 py-3 text-xs text-gray-500 max-w-xs truncate" title={assignedClients.map(c => c.name).join(', ')}>
                           {assignedClients.length > 0 ? assignedClients.map(c => c.name).join(', ') : <span className="text-gray-400 italic">N/A</span>}
                       </td>
                       <td className="px-4 py-3">
                         <div className="flex items-center space-x-1 flex-wrap gap-1">
                           <button onClick={() => handleServerAction(server.id, 'start')} className="p-1.5 text-gray-500 rounded-full hover:bg-green-100 hover:text-green-600 transition-colors" title="Iniciar"><Play className="w-4 h-4" /></button>
                           <button onClick={() => handleServerAction(server.id, 'stop')} className="p-1.5 text-gray-500 rounded-full hover:bg-red-100 hover:text-red-600 transition-colors" title="Detener"><Square className="w-4 h-4" /></button>
                           <button onClick={() => handleServerAction(server.id, 'restart')} className="p-1.5 text-gray-500 rounded-full hover:bg-blue-100 hover:text-blue-600 transition-colors" title="Reiniciar"><RotateCcw className="w-4 h-4" /></button>
                           <button onClick={() => setShowTrafficModal(server)} className="p-1.5 text-gray-500 rounded-full hover:bg-purple-100 hover:text-purple-600 transition-colors" title="Ver Tráfico">
                               <BarChart2 className="w-4 h-4" />
                           </button>
                           <button onClick={() => handleRenewServer(server)} className="p-1.5 text-gray-500 rounded-full hover:bg-green-100 hover:text-green-600 transition-colors" title="Renovar 1 mes"><CalendarPlus className="w-4 h-4" /></button>
                           <button onClick={() => handleEditClick(server)} className="p-1.5 text-gray-500 rounded-full hover:bg-yellow-100 hover:text-yellow-600 transition-colors" title="Editar"><Edit className="w-4 h-4" /></button>
                           <button onClick={() => handleAssignClick(server)} className="p-1.5 text-gray-500 rounded-full hover:bg-indigo-100 hover:text-indigo-600 transition-colors" title="Asignar"><Link className="w-4 h-4" /></button>
                           <button onClick={() => handleToggleLock(server.id)} className={`p-1.5 rounded-full transition-colors ${server.isLocked ? "text-yellow-600 hover:bg-yellow-100" : "text-gray-500 hover:bg-gray-100"}`} title={server.isLocked ? "Desbloquear" : "Bloquear"}>
                             {server.isLocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                           </button>
                           <button onClick={() => handleDeleteServer(server.id)} className="p-1.5 text-gray-500 rounded-full hover:bg-red-100 hover:text-red-600 transition-colors" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                         </div>
                       </td>
                     </tr>
                   );
                 })
               )}
             </tbody>
           </table>
         </div>

         {/* --- MODAL DE TRÁFICO --- */}
         {showTrafficModal && (
           <ServerTrafficModal 
             server={showTrafficModal} 
             onClose={() => setShowTrafficModal(null)} 
            // isClientPanel={false} // <- No es necesario, 'false' es el por defecto
           />
         )}
         
         {/* --- MODAL ASIGNAR --- */}
         {showAssignModal && selectedServer && (
           <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
             <div className="bg-white p-5 md:p-6 rounded-lg shadow-xl w-full max-w-md">
               <div className="flex justify-between items-center mb-4">
                   <h3 className="text-lg font-semibold text-gray-800">Asignar Clientes a: <span className="font-bold">{selectedServer.name}</span></h3>
                   <button onClick={() => setShowAssignModal(false)} className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                       <XCircle className="w-5 h-5"/>
                   </button>
               </div>
               <div className="space-y-2 max-h-60 overflow-y-auto border rounded-md p-3 mb-4">
                 {clients.length === 0 ? (
                   <p className="text-gray-500 italic">No hay clientes para asignar.</p>
                 ) : (
                   clients.map((client) => (
                       <div key={client.id} className="flex items-center p-1 hover:bg-gray-50 rounded">
                         <input
                             type="checkbox"
                             id={`client-assign-${client.id}`}
                             checked={assignModalClientIds.includes(client.id)}
                             className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                             onChange={(e) => handleAssignCheckboxChange(client.id, e.target.checked)}
                           />
                         <label htmlFor={`client-assign-${client.id}`} className="ml-2 text-sm text-gray-700 cursor-pointer">{client.name}</label>
                       </div>
                   ))
                 )}
               </div>
               <div className="flex justify-end space-x-3 pt-4 border-t">
                   <button type="button" onClick={() => setShowAssignModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors">Cancelar</button>
                   <button type="button" onClick={handleAssignServer} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors">Guardar Asignaciones</button>
               </div>
             </div>
           </div>
         )}

         {/* --- MODAL CREAR --- */}
       {showCreateModal && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
             <div className="bg-white p-5 md:p-6 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
               <div className="flex justify-between items-center mb-4">
                   <h3 className="text-lg font-semibold text-gray-800">Nuevo Servidor</h3>
                   <button onClick={() => setShowCreateModal(false)} className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                       <XCircle className="w-5 h-5"/>
                   </button>
               </div>
               <form onSubmit={handleCreateServer} className="space-y-3">
                 <InputField label="Nombre" value={newServer.name} onChange={(e) => setNewServer({ ...newServer, name: e.target.value })} required />
                 <InputField label="IP" value={newServer.ip} onChange={(e) => setNewServer({ ...newServer, ip: e.target.value })} required />
                 <InputField label="Usuario SSH" value={newServer.sshUser} onChange={(e) => setNewServer({ ...newServer, sshUser: e.target.value })} />
                 <InputField label="Contraseña SSH" type="password" value={newServer.sshPass} onChange={(e) => setNewServer({ ...newServer, sshPass: e.target.value })} />
                 <InputField label="ID Servicio (DCImanager o Royale)" value={newServer.royaleServiceId} onChange={(e) => setNewServer({ ...newServer, royaleServiceId: e.target.value })} />
                 <InputField label="ID de Precio de Stripe" placeholder="price_..." value={newServer.stripePriceId} onChange={(e) => setNewServer({ ...newServer, stripePriceId: e.target.value })} />
                 <InputField label="Fecha Vencimiento" type="date" value={newServer.expirationDate} onChange={(e) => setNewServer({ ...newServer, expirationDate: e.target.value })} />
                 <SelectField label="Proveedor" value={newServer.provider} onChange={(e) => setNewServer({ ...newServer, provider: e.target.value as 'royale' | 'dcimanager' })}>
                     <option value="dcimanager">DCImanager</option>
                     <option value="royale">Royale Hosting</option>
                 </SelectField>

                 <div className="flex justify-end space-x-3 pt-4">
                   <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors">Cancelar</button>
                   <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">Crear Servidor</button>
                 </div>
               </form>
             </div>
         </div>
         )}

         {/* --- MODAL EDITAR --- */}
         {showEditModal && editingServer && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 transition-opacity duration-300">
           <div className="bg-white p-5 md:p-6 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
             <div className="flex justify-between items-center mb-4">
                 <h3 className="text-lg font-semibold text-gray-800">Editando: {editingServer.name}</h3>
                 <button onClick={() => setShowEditModal(false)} className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                     <XCircle className="w-5 h-5"/>
                 </button>
             </div>
             <form onSubmit={handleUpdateServer} className="space-y-3">
               <InputField label="Nombre" value={editingServer.name} onChange={(e) => setEditingServer({ ...editingServer, name: e.target.value })} required />
               <InputField label="IP" value={editingServer.ip} onChange={(e) => setEditingServer({ ...editingServer, ip: e.target.value })} required />
               <InputField label="Usuario SSH" value={editingServer.sshUser || ''} onChange={(e) => setEditingServer({ ...editingServer, sshUser: e.target.value })} />
               <InputField label="Contraseña SSH" type="text" value={editingServer.sshPass || ''} onChange={(e) => setEditingServer({ ...editingServer, sshPass: e.target.value })} />
               <InputField label="ID Servicio" value={editingServer.royaleServiceId || ''} onChange={(e) => setEditingServer({ ...editingServer, royaleServiceId: e.target.value })} />
               <InputField label="ID de Precio de Stripe" placeholder="price_..." value={editingServer.stripePriceId || ''} onChange={(e) => setEditingServer({ ...editingServer, stripePriceId: e.target.value })} />
               <InputField label="Fecha Vencimiento" type="date" value={editingServer.expirationDate || ''} onChange={(e) => setEditingServer({ ...editingServer, expirationDate: e.target.value })} />
               <SelectField label="Proveedor" value={editingServer.provider} onChange={(e) => setEditingServer({ ...editingServer, provider: e.target.value as 'royale' | 'dcimanager' })}>
                   <option value="dcimanager">DCImanager</option>
                   <option value="royale">Royale Hosting</option>
               </SelectField>
               <div className="flex justify-end space-x-3 pt-4">
                 <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors">Cancelar</button>
                 <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors">Guardar Cambios</button>
               </div>
             </form>
           </div>
         </div>
         )}

     </div> // Cierre del div principal
   );
};


// --- Componentes auxiliares para los formularios ---
interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
   label: string;
}
const InputField: React.FC<InputFieldProps> = ({ label, ...props }) => (
   <div>
     <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
     <input
       {...props}
       className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
     />
   </div>
);

interface SelectFieldProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
     label: string;
}
const SelectField: React.FC<SelectFieldProps> = ({ label, children, ...props }) => (
     <div>
         <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
         <select
             {...props}
             className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
         >
             {children}
         </select>
     </div>
);